/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useEffect, useState } from 'react'
import { View, Text, BoxView, ActionButton } from '@components/atoms'
import {
	SeasonRegistrationModal,
	SeasonQueueOrderWarningModal,
	SeasonOpenConfirmationModal,
	SeasonOpenPeriodWarningModal,
	SeasonDeleteConfirmationModal,
	SeasonCloseConfirmationModal,
} from '@components/molecules'
import { SeasonList, SeasonQueueList } from '@components/organisms'
import { Tabs, Tab } from '@mui/material'
import { WarningAmber } from '@mui/icons-material'

import { useAppDispatch, useAppSelector, seasonActions, routeActions } from '@redux/index'
import {
	SeasonProductAddType,
	SeasonProductBatchUpdateType,
	SeasonProductDeleteType,
	SeasonProductType,
	SeasonProductUpdateType,
	SeasonType,
	SeasonModificationType,
} from '@schemas'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { SeasonProductList, SeasonOverview } from '@components/organisms'
import { mono, boShared, boMedia } from '@components/systems/backoffice/backoffice.tokens'
import { BoSplit } from '@components/systems/backoffice/BoSplit'

const SeasonPage = () => {
	const dispatch = useAppDispatch()
	const { selectedSeason, selectedYear, seasonProducts, loading, products } =
		useAppSelector(state => state.season)
	const seasonList: SeasonType[] = useAppSelector(state => state.season.seasonList)
	const [modalOpen, setModalOpen] = useState(false)
	const [currentSeasonProducts, setCurrentSeasonProducts] = useState<SeasonProductType[]>(
		[],
	)
	const [activeTab, setActiveTab] = useState<'list' | 'queue'>('list')
	// 대기열 추가 시 날짜 역전이 감지되면 확인을 받는다
	const [showOpenConfirm, setShowOpenConfirm] = useState(false)
	const [showPeriodWarning, setShowPeriodWarning] = useState(false)
	const [showCloseConfirm, setShowCloseConfirm] = useState(false)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [queueWarning, setQueueWarning] = useState<{
		target: SeasonType
		conflict: SeasonType
	} | null>(null)

	/**
	 * 지금 shop 에 노출중인 시즌.
	 *
	 * seasonList 는 선택 연도로 필터되므로 다른 연도를 보면 사라진다.
	 * 배너는 연도와 무관하게 고정돼야 하니 서버에서 따로 받은 값을 쓴다.
	 */
	const openSeason: SeasonType | null = useAppSelector(state => state.season.openSeason)

	/**
	 * 대기열 (queueOrder 오름차순).
	 *
	 * seasonList 에서 걸러내면 연도를 바꾸는 순간 다른 연도의 대기 시즌이 사라진다.
	 * 배너와 같이 `GET /season/open` 이 연도와 무관하게 돌려주는 목록을 쓴다.
	 */
	const queueList: SeasonType[] = useAppSelector(state => state.season.queueList)

	/**
	 * 판매중 시즌의 종료가 임박했는데 대기열이 비어 있으면 경고한다.
	 * 이번처럼 다음 시즌 없이 판매가 조용히 멈추는 것을 막기 위한 장치다.
	 */
	const endingSoonWarning: string | null = React.useMemo(() => {
		if (openSeason === null) {
			return '현재 판매 중지 상태입니다 — shop 의 전 상품이 Sold Out 으로 표시됩니다.'
		}

		const daysLeft = dayjs(openSeason.endDate)
			.startOf('day')
			.diff(dayjs().startOf('day'), 'day')

		if (daysLeft < 0) {
			return '종료일이 지난 시즌이 아직 판매중입니다. 다음 시즌으로 전환하세요.'
		}

		if (daysLeft <= 7 && queueList.length === 0) {
			return `${openSeason.title}이(가) ${dayjs(openSeason.endDate).format(
				'M월 D일',
			)} 종료됩니다. 다음 시즌을 준비하세요.`
		}

		return null
	}, [openSeason, queueList])

	useEffect(() => {
		if (seasonProducts) {
			setCurrentSeasonProducts(JSON.parse(JSON.stringify(seasonProducts)))
		}
	}, [seasonProducts])

	const handleSeasonClick = (season: SeasonType) => {
		dispatch(seasonActions.SET_SELECTED_SEASON(season))
		dispatch(seasonActions.FETCH_SEASON_PRODUCT(season.id))
	}
	const handleYearChange = (newValue: Date | null) => {
		if (newValue) {
			dispatch(seasonActions.SET_SELECTED_YEAR(dayjs(newValue).format('YYYY')))
		}
	}

	/**
	 * 대기열 추가.
	 *
	 * 앞 순번에 더 늦게 시작하는 시즌이 있으면(날짜 역전) 확인을 받는다.
	 * 역전이 없으면 그대로 맨 뒤에 붙인다. (SEASON_OPEN_PLAN.md §7.1)
	 */
	const handleAddToQueue = (season: SeasonType) => {
		const conflict = queueList.find(anExisting =>
			dayjs(anExisting.startDate).isAfter(dayjs(season.startDate)),
		)

		if (conflict) {
			setQueueWarning({ target: season, conflict: conflict })
			return
		}

		dispatch(
			seasonActions.UPDATE_SEASON_QUEUE([
				...queueList.map(aSeason => aSeason.id),
				season.id,
			]),
		)
		setActiveTab('queue')
	}

	/** 경고에도 그대로 맨 뒤에 추가 */
	const handleQueueKeepOrder = () => {
		if (queueWarning === null) return

		dispatch(
			seasonActions.UPDATE_SEASON_QUEUE([
				...queueList.map(aSeason => aSeason.id),
				queueWarning.target.id,
			]),
		)
		setQueueWarning(null)
		setActiveTab('queue')
	}

	/** 시작일 기준으로 올바른 위치에 삽입 */
	const handleQueueMoveForward = () => {
		if (queueWarning === null) return

		const target = queueWarning.target
		const insertIdx = queueList.findIndex(anExisting =>
			dayjs(anExisting.startDate).isAfter(dayjs(target.startDate)),
		)
		const newList = [...queueList]
		newList.splice(insertIdx === -1 ? newList.length : insertIdx, 0, target)

		dispatch(seasonActions.UPDATE_SEASON_QUEUE(newList.map(aSeason => aSeason.id)))
		setQueueWarning(null)
		setActiveTab('queue')
	}

	const handleOpenModal = () => {
		setModalOpen(true)
	}

	const handleCloseModal = () => {
		setModalOpen(false)
	}

	/*
		판매 시작/중지와 삭제. 전에는 `/main/season/:id/edit` 전체 화면에 있었는데,
		시즌 하나를 손보는 일이 전부 이 화면에서 끝나므로 개요 패널로 옮겼다.
		되돌리기 어려운 조작이라 확인 모달은 그대로 가져왔다 (SEASON_OPEN_PLAN §4).
	*/
	const handleOpenSaleClick = () => {
		if (selectedSeason === null) return
		dispatch(seasonActions.CLEAR_OPEN_ERROR())

		// 기간 밖이면 서버까지 가지 않고 여기서 막는다. 강행 경로는 없다.
		const today = dayjs().startOf('day')
		const inPeriod =
			!today.isBefore(dayjs(selectedSeason.startDate).startOf('day')) &&
			!today.isAfter(dayjs(selectedSeason.endDate).startOf('day'))

		if (!inPeriod) {
			setShowPeriodWarning(true)
			return
		}
		setShowOpenConfirm(true)
	}

	const handleOpenConfirm = () => {
		if (selectedSeason === null) return
		dispatch(seasonActions.OPEN_SEASON(selectedSeason.id))
		setShowOpenConfirm(false)
	}

	const handleCloseConfirm = () => {
		dispatch(seasonActions.CLOSE_SEASON())
		setShowCloseConfirm(false)
	}

	const handleDeleteConfirm = () => {
		if (selectedSeason === null) return
		dispatch(seasonActions.DELETE_SEASON(selectedSeason.id))
		setShowDeleteConfirm(false)
	}

	/** 상단 개요에서 시즌 정보를 고쳤을 때. 전체 화면 수정과 같은 액션을 쓴다. */
	const handleSeasonSave = (body: SeasonModificationType) => {
		if (selectedSeason === null) return
		dispatch(seasonActions.UPDATE_SEASON({ id: selectedSeason.id, season: body }))
	}

	/**
	 * 시즌 상품 상세(전체 화면)로 간다. 판매 단계와 이행 로드맵이 거기 있다.
	 * 목록이 비어 있는 채로 직접 들어와도 되도록 선택한 행을 스토어에 실어 보낸다.
	 */
	const handleSeasonProductDetailClick = (seasonProduct: SeasonProductType) => {
		dispatch(seasonActions.SET_SELECTED_SEASON_PRODUCT(seasonProduct))
		dispatch(
			routeActions.NAVIGATE_PATH(
				`/main/season/${seasonProduct.seasonId}/product/${seasonProduct.id}`,
			),
		)
	}

	const handleUpdateSeasonProduct = (
		updatedProducts: SeasonProductBatchUpdateType[],
		deletedProducts: SeasonProductType[],
	) => {
		const batchUpdatePayload: SeasonProductBatchUpdateType[] = updatedProducts.map(
			product => {
				if (product.iudFlag === 'I') {
					return {
						iudFlag: 'I',
						seasonId: selectedSeason?.id,
						productId: product.productId,
						usd: product.usd,
						krw: product.krw,
						discountUsd: product.discountUsd,
						quantity: product.quantity,
						activeYn: product.activeYn,
						preorderYn: product.preorderYn,
					} as SeasonProductAddType
				} else if (product.iudFlag === 'U') {
					return {
						iudFlag: 'U',
						id: product.id,
						seasonId: selectedSeason?.id,
						productId: product.productId,
						usd: product.usd,
						krw: product.krw,
						quantity: product.quantity,
						discountUsd: product.discountUsd,
						activeYn: product.activeYn,
						preorderYn: product.preorderYn,
						partOrderedAt: product.partOrderedAt,
						productionStartAt: product.productionStartAt,
						deliveryStartAt: product.deliveryStartAt,
						deliveryCompletedAt: product.deliveryCompletedAt,
					} as SeasonProductUpdateType
				} else {
					return {
						iudFlag: 'D',
						id: product.id,
					} as SeasonProductDeleteType
				}
			},
		)

		dispatch(seasonActions.BATCH_UPDATE_SEASON_PRODUCT([...batchUpdatePayload]))
	}

	return (
		<View css={style}>
			<BoSplit
				className="seasonOuterView"
				title="시즌 세부 정보"
				detailKey={selectedSeason ? String(selectedSeason.id) : ''}
				list={
					<BoxView
						title="시즌 리스트"
						className="seasonView"
						rightHeaderAccessory={
							<ActionButton
								title="시즌 등록"
								iconName="plus"
								buttonColor="primary"
								variant="contained"
								onClick={handleOpenModal}
							/>
						}
					>
						<View className="seasonContentView">
							{/* 지금 shop 에 노출중인 시즌을 목록 위에 고정해 한눈에 보이게 한다 */}
							<View className={`openBannerView ${openSeason ? 'open' : 'closed'}`}>
								<View className="openBannerHeader">
									<span className={`boChip ${openSeason ? 'solid' : 'faint'}`}>
										{openSeason ? '판매 중' : '판매 중지'}
									</span>
									<Text className="openBannerTitle">
										{openSeason ? openSeason.title : '판매중인 시즌 없음'}
									</Text>
								</View>

								{openSeason && (
									<Text className="openBannerDate">
										{`${dayjs(openSeason.startDate).format('YYYY-MM-DD')} ~ ${dayjs(
											openSeason.endDate,
										).format('YYYY-MM-DD')}`}
									</Text>
								)}

								{endingSoonWarning !== null && (
									<View className="openBannerWarning">
										<WarningAmber fontSize="small" />
										<Text className="openBannerWarningText">{endingSoonWarning}</Text>
									</View>
								)}
							</View>

							<Tabs
								value={activeTab}
								onChange={(_, newValue) => setActiveTab(newValue as 'list' | 'queue')}
								className="seasonTabs"
							>
								<Tab label="시즌 목록" value="list" />
								<Tab
									label={
										queueList.length > 0
											? `판매 대기열 (${queueList.length})`
											: '판매 대기열'
									}
									value="queue"
								/>
							</Tabs>

							<View className="seasonViewContentHeader">
								<DatePicker
									label="해당연도"
									value={dayjs(`${selectedYear as string}-01-01`).toDate()}
									onChange={handleYearChange}
									views={['year']}
									slotProps={{
										textField: {
											variant: 'outlined',
											size: 'small',
											style: {
												width: '8rem',
											},
										},
									}}
									format="yyyy-MM-dd"
									minDate={new Date('2025-01-01')}
								/>
							</View>
							<View className="listOuterView">
								{loading ? (
									<p className="boEmpty">Loading...</p>
								) : activeTab === 'list' ? (
									<SeasonList
										seasons={seasonList}
										selectedSeasonId={selectedSeason === null ? '' : selectedSeason.id}
										onSeasonClick={handleSeasonClick}
										onAddToQueue={handleAddToQueue}
									/>
								) : (
									<View className="queueOuterView">
										<SeasonQueueList
											queueList={queueList}
											selectedSeasonId={selectedSeason === null ? '' : selectedSeason.id}
											onSeasonClick={handleSeasonClick}
											// 판매 시작은 세부 정보 패널에 있다. 여기서는 그 시즌을 고르기만 한다.
											onOpenClick={handleSeasonClick}
										/>
									</View>
								)}
							</View>
						</View>
					</BoxView>
				}
				detail={
					<BoxView
						title={`시즌 세부 정보 ${
							selectedSeason ? `- ${selectedSeason.title as string}` : ''
						}`}
						className="seasonProductView"
					>
						{selectedSeason ? (
							/*
							BoxView 의 `.boxChildrenView` 가 `display: flex` 에 방향이 기본값(row)이다.
							자식이 하나일 때는 드러나지 않았지만 둘이 되면 가로로 나란히 선다.
							BoxView 는 다른 화면도 쓰므로 건드리지 않고 여기서 세로로 쌓는다.
						*/
							<View className="seasonPanelStack">
								<SeasonOverview
									season={selectedSeason}
									onSave={handleSeasonSave}
									onOpenSale={handleOpenSaleClick}
									onCloseSale={() => setShowCloseConfirm(true)}
									onDelete={() => setShowDeleteConfirm(true)}
								/>
								<SeasonProductList
									products={products}
									seasonProducts={currentSeasonProducts}
									onUpdateSeasonProduct={handleUpdateSeasonProduct}
									onDetailClick={handleSeasonProductDetailClick}
								/>
							</View>
						) : (
							<p className="boEmpty">Select a season to view its products</p>
						)}
					</BoxView>
				}
			/>
			<SeasonRegistrationModal open={modalOpen} onClose={handleCloseModal} />

			<SeasonOpenConfirmationModal
				open={showOpenConfirm}
				currentSeason={openSeason}
				targetSeason={selectedSeason}
				onCancel={() => setShowOpenConfirm(false)}
				onConfirm={handleOpenConfirm}
			/>

			<SeasonOpenPeriodWarningModal
				open={showPeriodWarning}
				season={selectedSeason}
				onClose={() => {
					setShowPeriodWarning(false)
					dispatch(seasonActions.CLEAR_OPEN_ERROR())
				}}
			/>

			<SeasonCloseConfirmationModal
				open={showCloseConfirm}
				onClose={() => setShowCloseConfirm(false)}
				onConfirm={handleCloseConfirm}
				seasonTitle={selectedSeason?.title ?? ''}
			/>

			<SeasonDeleteConfirmationModal
				open={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				onDelete={handleDeleteConfirm}
				seasonTitle={selectedSeason?.title ?? ''}
			/>

			<SeasonQueueOrderWarningModal
				open={queueWarning !== null}
				targetSeason={queueWarning?.target ?? null}
				conflictSeason={queueWarning?.conflict ?? null}
				onKeepOrder={handleQueueKeepOrder}
				onMoveForward={handleQueueMoveForward}
				onClose={() => setQueueWarning(null)}
			/>
		</View>
	)
}

export default SeasonPage

const style = css({
	...boShared,
	height: '100%',

	'.seasonOuterView': {
		position: 'relative',
		display: 'flex',
		height: '100%',

		// 좌 목록 / 우 세부 — 두 칸을 헤어라인 하나로 가른다
		'.seasonView': {
			width: '30rem',
			flexShrink: 0,
			paddingRight: 28,
			borderRight: `1px solid ${mono.line}`,

			'.seasonContentView': {
				display: 'flex',
				flexDirection: 'column',
				gap: 16,
				width: '100%',
				height: '100%',

				// 지금 shop 에 노출중인 시즌 — 박스 대신 헤어라인 밴드
				'.openBannerView': {
					padding: '4px 0 16px',
					borderRadius: 0,
					borderBottom: `1px solid ${mono.line}`,

					'.openBannerHeader': {
						display: 'flex',
						alignItems: 'center',
						gap: 10,

						'.openBannerTitle': {
							margin: 0,
							fontFamily: 'inherit',
							fontSize: 17,
							fontWeight: 400,
							color: mono.ink,
							width: 'auto',
						},
					},

					'.openBannerDate': {
						display: 'block',
						margin: '6px 0 0',
						fontFamily: 'inherit',
						color: mono.dim,
						fontSize: 12.5,
						fontWeight: 300,
						fontVariantNumeric: 'tabular-nums',
					},

					'.openBannerWarning': {
						display: 'flex',
						alignItems: 'flex-start',
						gap: 6,
						marginTop: 10,
						color: mono.ink,

						'.MuiSvgIcon-root': { fontSize: 16, marginTop: 1, color: mono.dim },

						'.openBannerWarningText': {
							margin: 0,
							fontFamily: 'inherit',
							fontSize: 12.5,
							fontWeight: 400,
							lineHeight: 1.55,
						},
					},
				},

				'.seasonTabs': {
					minHeight: 36,
					borderBottom: `1px solid ${mono.line}`,
					marginBottom: -8,

					'.MuiTab-root': {
						minHeight: 36,
						padding: '0 4px',
						marginRight: 20,
						fontWeight: 500,
					},
				},

				'.queueOuterView': {
					padding: 0,
				},

				'.seasonViewContentHeader': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					width: '100%',
					marginBottom: -8,
					paddingTop: 8,
				},

				'.listOuterView': {
					width: '100%',
					height: '100%',
					padding: 0,
					border: 0,
					borderTop: `1px solid ${mono.line}`,
					borderRadius: 0,
					backgroundColor: 'transparent',
					overflowY: 'auto',
				},
			},
		},
		'.seasonProductView': {
			width: 'calc(100% - 30rem)',
			paddingLeft: 28,
		},

		[boMedia.notDesktop]: {
			flexDirection: 'column',
			height: 'auto',
			'.seasonView': {
				width: '100%',
				paddingRight: 0,
				borderRight: 0,
				borderBottom: `1px solid ${mono.line}`,
				marginBottom: 16,
			},
			'.seasonProductView': { width: '100%', paddingLeft: 0 },
		},

		'.seasonPanelStack': {
			display: 'flex',
			flexDirection: 'column',
			width: '100%',
		},
	},
})
